import { CircleCheck, Info, Loader } from "lucide-react";

import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "@/components/ui/popover";
import RuvixxDate from "components/RuvixxDate";

const OBJECT_LABELS = {
  event: "Event",
  session: "Session",
  registrant: "Registrant",
  session_attendance: "Attendance",
};

const getTitle = ({ isFailed, hasGaps }) => {
  if (isFailed) {
    return "Sync failed";
  }
  return hasGaps ? "Finished with gaps" : "All synced";
};

const SkippedList = ({ skipped, skippedCount }) => {
  const omittedCount = skippedCount - skipped.length;

  return (
    <>
      <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-2 tw:p-0">
        {skipped.map(record => (
          <li
            key={`${record.object_type}-${record.id}`}
            className="tw:flex tw:flex-col tw:gap-0.5"
          >
            <span>
              {OBJECT_LABELS[record.object_type] ?? record.object_type}{" "}
              {record.id}
            </span>
            <span className="tw:text-xs tw:text-[var(--color-text-secondary)]">
              {record.reason}
            </span>
          </li>
        ))}
      </ul>
      {omittedCount > 0 && (
        <span className="tw:text-xs tw:text-[var(--color-text-muted)]">
          {omittedCount} more not listed.
        </span>
      )}
    </>
  );
};

// Clickable status indicator for the Swoogo sync. A clean run reads as a calm
// green check; a failed run, or one that skipped records, reads as a muted info
// icon whose popover explains what is missing. A pending sync shows a spinner
// and no popover; a never-synced account shows nothing.
const SwoogoSyncStatusPopover = ({
  syncStatus,
  lastSyncedAt,
  syncError,
  skipped = [],
  skippedCount = 0,
}) => {
  if (syncStatus === "pending") {
    return (
      <Loader
        className="tw:size-3.5 tw:animate-spin tw:text-[var(--color-text-muted)]"
        aria-label="Sync in progress"
      />
    );
  }

  if (syncStatus !== "success" && syncStatus !== "error") {
    return null;
  }

  const isFailed = syncStatus === "error";
  const hasGaps = !isFailed && skippedCount > 0;
  const title = getTitle({ isFailed, hasGaps });

  return (
    <Popover>
      <PopoverTrigger
        aria-label="Sync status"
        className="tw:inline-flex tw:cursor-pointer tw:items-center tw:rounded-sm tw:outline-hidden focus-visible:tw:ring-1 focus-visible:tw:ring-[var(--color-border)]"
      >
        {isFailed || hasGaps ? (
          <Info
            data-testid="swoogo-sync-status-attention"
            className="tw:size-3.5 tw:text-[var(--color-text-muted)]"
          />
        ) : (
          <CircleCheck
            data-testid="swoogo-sync-status-success"
            className="tw:size-3.5 tw:text-[var(--color-teal)]"
          />
        )}
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="tw:w-80 tw:bg-[var(--color-surface-overlay)] tw:text-[var(--color-text-primary)]"
      >
        <PopoverHeader>
          <PopoverTitle>{title}</PopoverTitle>
          {lastSyncedAt && (
            <span className="tw:text-xs tw:text-[var(--color-text-secondary)]">
              Last synced{" "}
              <RuvixxDate date={lastSyncedAt} isAbsoluteDateEnabled />
            </span>
          )}
        </PopoverHeader>
        {isFailed && (
          <span className="tw:text-xs tw:text-[var(--color-text-secondary)] tw:select-text">
            {syncError || "The last sync failed."}
          </span>
        )}
        {hasGaps && (
          <>
            <span className="tw:text-xs tw:text-[var(--color-text-secondary)]">
              These records could not be stored. A record returns in the next
              sync once it is corrected in Swoogo.
            </span>
            <SkippedList skipped={skipped} skippedCount={skippedCount} />
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default SwoogoSyncStatusPopover;
